import React from "react";
import {
  Landmark, Receipt, AlertTriangle, Info, Users, PieChart, FileWarning,
  Sparkles, Loader2,
} from "lucide-react";
import * as api from "../api/chargeback";
import { card, th, td, btn, Kpi, useAsync, Loading, Err, n, money } from "./cyberShared";
import { RenewalTimeline, UtilisationQuadrant } from "../features/chargeback/charts";

/**
 * Program cost allocation — showback, and the basis for chargeback.
 *
 * The estate has always recorded what technology costs. It has never recorded
 * whose budget it lands on, which in an appropriated agency is the difference
 * between a spend report and a bill. Every figure here carries the basis it was
 * derived from, because a charge a program office cannot interrogate is a charge
 * they will dispute.
 */

/** Renders the model's plan: headings, bold, and nothing else ever appears. */
function Plan({ text }) {
  const bold = (line, key) =>
    String(line).split(/\*\*(.+?)\*\*/g).map((part, i) =>
      i % 2 === 1
        ? <strong key={`${key}-${i}`} style={{ fontWeight: 700, color: "var(--cares-text-1)" }}>{part}</strong>
        : <span key={`${key}-${i}`}>{part}</span>);
  return (
    <div style={{ fontSize: 12.5, lineHeight: 1.62, color: "var(--cares-text-2)" }}>
      {String(text || "").split("\n").map((raw, i) => {
        const line = raw.trim();
        if (!line) return <div key={i} style={{ height: 8 }} />;
        if (line.startsWith("## ")) {
          return (
            <h4 key={i} style={{ fontSize: 13, fontWeight: 700, color: "var(--cares-text-1)",
                                 margin: "15px 0 5px" }}>{line.slice(3)}</h4>
          );
        }
        return <p key={i} style={{ margin: "0 0 5px" }}>{bold(line, i)}</p>;
      })}
    </div>
  );
}

const BASIS_TONE = {
  direct: { bg: "color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))", fg: "var(--cares-purple)" },
  headcount: { bg: "var(--cares-surface-board)", fg: "var(--cares-text-3)" },
  devices: { bg: "color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))", fg: "var(--cares-green)" },
  seats: { bg: "color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))", fg: "var(--cares-amber)" },
  negotiated: { bg: "color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))", fg: "var(--cares-purple)" },
};

const Pill = ({ tone, children }) => (
  <span style={{ background: tone.bg, color: tone.fg, fontSize: 10, fontWeight: 700,
                 padding: "2px 7px", borderRadius: 999, whiteSpace: "nowrap" }}>
    {children}
  </span>
);

function ShareBar({ pct, tone = "var(--cares-purple)" }) {
  return (
    <div style={{ height: 6, background: "var(--cares-surface-board)", borderRadius: 3, minWidth: 60 }}>
      <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", borderRadius: 3,
                    background: tone }} />
    </div>
  );
}


// Spend on AI-capable products against the governance state of those products.
// The framing is deliberate: this is the full licence value of anything carrying
// an AI capability, which is a governance surface, not the cost of AI. Vendors do
// not price the AI portion separately, so any figure claiming to be "AI spend"
// would be inventing an apportionment nobody could defend.
function AiExposure({ d }) {
  const g = d.governanceExposure || {};
  const cb = d.capabilityBreakdown || {};
  const maxCost = Math.max(...(d.byProgram || []).map((p) => p.aiCost), 1);

  return (
    <div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Kpi label="AI-enabled spend" value={money(d.aiEnabledSpend)}
             sub={`${d.estateShare.aiEnabledPct}% of the estate`} />
        <Kpi label="Systems in inventory" value={n(d.systemCount)}
             sub={`${cb.publiclyKnownCapability?.systems ?? 0} publicly known`} />
        <Kpi label="No impact assessment" value={money(g.unassessedSpend)}
             tone="var(--cares-orange)" sub={`${g.unassessedPct}% of AI-enabled spend`} />
        <Kpi label="Rights-impacting" value={money(g.rightsImpactingSpend)}
             tone="var(--cares-amber)" sub="spend on rights-impacting systems" />
      </div>

      <div style={{ ...card, padding: 12, marginBottom: 14, background: "color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))",
                    border: "1px solid color-mix(in srgb, var(--cares-amber) 30%, var(--cares-surface-card))" }}>
        <div style={{ fontSize: 12, color: "var(--cares-amber)", lineHeight: 1.55 }}>
          <strong>This is not AI consumption chargeback.</strong> No token, inference
          or GPU metering sits behind these figures. They are the full contract value
          of products that carry an AI capability, allocated on the same recorded
          bases as everything else — {cb.embeddedOrInferred?.systems ?? 0} of the{" "}
          {d.systemCount} carry only an embedded or inferred ML feature, and the whole
          licence is counted for those because no vendor prices the AI portion
          separately. Read it as governance surface, not cost of AI.
        </div>
      </div>

      <div style={{ ...card, padding: 16, marginBottom: 14 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 3px" }}>
          Which offices fund the AI footprint
        </h3>
        <p style={{ fontSize: 11.5, color: "var(--cares-text-note)", margin: "0 0 10px" }}>
          The right-hand column is the one that starts conversations: how much of an
          office&rsquo;s own bill is already sitting in AI-capable products.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>Office</th>
            <th style={th}>Appropriation</th>
            <th style={{ ...th, textAlign: "right" }}>AI-enabled</th>
            <th style={th}>Share</th>
            <th style={{ ...th, textAlign: "right" }}>Of their bill</th>
          </tr></thead>
          <tbody>
            {(d.byProgram || []).map((p) => (
              <tr key={p.program_code}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{p.program_code}</div>
                  <div style={{ fontSize: 11, color: "var(--cares-text-note)" }}>{p.program_name}</div>
                </td>
                <td style={{ ...td, fontSize: 11, color: "var(--cares-text-label)" }}>{p.appropriation}</td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {money(p.aiCost)}
                </td>
                <td style={{ ...td, minWidth: 90 }}>
                  <ShareBar pct={(p.aiCost / maxCost) * 100} />
                </td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums",
                             color: p.pctOfProgramBill > 75 ? "var(--cares-amber)" : "var(--cares-text-1)" }}>
                  {p.pctOfProgramBill == null ? "\u2014" : `${p.pctOfProgramBill}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ ...card, padding: 16 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 3px" }}>
          Spend against governance state
        </h3>
        <p style={{ fontSize: 11.5, color: "var(--cares-text-note)", margin: "0 0 10px" }}>
          Money already committed, set beside whether the system has cleared the
          gates a federal AI policy would ask for. The charge lands either way.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>System</th>
            <th style={th}>Basis</th>
            <th style={{ ...th, textAlign: "right" }}>Allocated</th>
            <th style={th}>Category</th>
            <th style={th}>Assessment</th>
          </tr></thead>
          <tbody>
            {(d.bySystem || []).map((r) => (
              <tr key={r.productId}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{r.system}</div>
                  <div style={{ fontSize: 11, color: "var(--cares-text-note)" }}>
                    {r.vendor}{r.publiclyKnownCapability ? "" : " \u00b7 embedded capability"}
                  </div>
                </td>
                <td style={{ ...td, fontSize: 11, color: "var(--cares-text-label)" }}>{r.basisLabel}</td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {money(r.cost)}
                </td>
                <td style={{ ...td, fontSize: 11 }}>
                  {r.rightsImpacting && <Pill tone={{ fg: "var(--cares-orange)", bg: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))" }}>rights</Pill>}
                  {r.safetyImpacting && <Pill tone={{ fg: "var(--cares-amber)", bg: "color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))" }}>safety</Pill>}
                  {!r.rightsImpacting && !r.safetyImpacting &&
                    <span style={{ color: "var(--cares-text-note)" }}>{"\u2014"}</span>}
                </td>
                <td style={td}>
                  {r.impactAssessmentComplete
                    ? <Pill tone={{ fg: "var(--cares-green)", bg: "color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))" }}>complete</Pill>
                    : <Pill tone={{ fg: "var(--cares-orange)", bg: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))" }}>not assessed</Pill>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// What was identified, and what anyone actually did about it. The renewal calendar
// says what could be recovered; this says what was decided. "Declined" is shown
// rather than hidden — a pipeline that only counts wins overstates the programme.
function SavingsPipeline({ d }) {
  const t = d.totals;
  const TONE = { identified: "var(--cares-text-note)", committed: "var(--cares-purple)",
                 realized: "var(--cares-green)", declined: "var(--cares-amber)" };
  const max = Math.max(...d.stages.map((s) => s.idleAnnual), 1);
  return (
    <div style={{ ...card, padding: 16, marginBottom: 14 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 3px" }}>
        Savings pipeline
      </h3>
      <p style={{ fontSize: 11.5, color: "var(--cares-text-note)", margin: "0 0 12px" }}>
        An idle licence nobody has decided about is not a saving. Every row below
        carries an owner and a decision date, so realisation can be audited against
        what was committed rather than asserted at year end.
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Kpi label="Identified" value={money(t.pipelineTotal)}
             sub={`${d.count} candidates`} />
        <Kpi label="Committed" value={money(t.committed)} tone="var(--cares-purple)"
             sub="owner and date agreed" />
        <Kpi label="Realised" value={money(t.realized)} tone="var(--cares-green)"
             sub={`${t.realisationRatePct}% of identified`} />
        <Kpi label="Awaiting decision" value={money(t.awaitingDecision)}
             tone={t.awaitingDecision > 0 ? "var(--cares-amber)" : "var(--cares-green)"}
             sub={`${t.decisionRatePct}% of candidates decided`} />
      </div>

      <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
        {d.stages.map((s) => (
          <div key={s.status} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 96, fontSize: 11.5, fontWeight: 600,
                          color: TONE[s.status], textTransform: "capitalize" }}>
              {s.status}
            </div>
            <div style={{ flex: 1, minWidth: 0, height: 8, background: "var(--cares-surface-board)",
                          borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${(s.idleAnnual / max) * 100}%`, height: "100%",
                            background: TONE[s.status] }} />
            </div>
            <div style={{ width: 104, textAlign: "right", fontSize: 12,
                          fontVariantNumeric: "tabular-nums" }}>
              {money(s.idleAnnual)}
            </div>
            <div style={{ width: 34, textAlign: "right", fontSize: 11, color: "var(--cares-text-note)" }}>
              {s.count}
            </div>
          </div>
        ))}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>Office</th>
            <th style={{ ...th, textAlign: "right" }}>Awaiting</th>
            <th style={{ ...th, textAlign: "right" }}>Committed</th>
            <th style={{ ...th, textAlign: "right" }}>Realised</th>
            <th style={{ ...th, textAlign: "right" }}>Declined</th>
          </tr></thead>
          <tbody>
            {d.byProgram.map((b) => (
              <tr key={b.programCode}>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{b.programCode}</div>
                  <div style={{ fontSize: 11, color: "var(--cares-text-note)" }}>{b.programName}</div>
                </td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(b.identified)}</td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--cares-purple)" }}>{money(b.committed)}</td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--cares-green)" }}>{money(b.realized)}</td>
                <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--cares-amber)" }}>{money(b.declined)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Colour of money. A charge that cannot name the object class it posts to and the
// availability of the appropriation behind it is an invoice, not a federal
// chargeback statement — availability constrains *when* a charge can be obligated,
// not merely who pays it.
function FundsControl({ fc }) {
  const AV = { "1-year": "#b45309", "multi-year": "#4f46e5", "no-year": "#059669" };
  return (
    <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--cares-line)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap",
                    marginBottom: 9 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cares-text-2)" }}>Funds control</span>
        <span style={{ fontSize: 11.5, color: "var(--cares-text-label)" }}>{fc.appropriation}</span>
        {fc.availability && (
          <span style={{ fontSize: 10.5, fontWeight: 600, padding: "1px 7px",
                         borderRadius: 999, color: AV[fc.availability] || "var(--cares-text-3)",
                         background: "var(--cares-surface-board)", border: "1px solid var(--cares-line-card)" }}>
            {fc.availability} · {fc.availabilityPeriod}
          </span>
        )}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {fc.objectClasses.map((o) => (
            <tr key={o.objectClass}>
              <td style={{ padding: "4px 0", fontSize: 11.5, fontFamily: "monospace",
                           color: "var(--cares-purple)", width: 46 }}>{o.objectClass}</td>
              <td style={{ padding: "4px 0", fontSize: 11.5, color: "var(--cares-text-3)" }}>
                {o.objectClassName}
              </td>
              <td style={{ padding: "4px 0", fontSize: 11.5, color: "var(--cares-text-note)",
                           textAlign: "right", width: 54 }}>{o.lines} lines</td>
              <td style={{ padding: "4px 0", fontSize: 12, textAlign: "right", width: 108,
                           fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                {money(o.charge)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 10.5, color: "var(--cares-text-note)", marginTop: 7 }}>{fc.note}</div>
    </div>
  );
}

const TABS = [
  { id: "allocation", label: "Who pays" },
  { id: "calendar", label: "When we can act" },
  { id: "utilisation", label: "What to cut" },
  { id: "ai", label: "AI exposure" },
];

export default function ChargebackPage() {
  const [tab, setTab] = React.useState("allocation");
  const [selected, setSelected] = React.useState(null);
  const [plan, setPlan] = React.useState(null);
  const [planning, setPlanning] = React.useState(false);
  const [planError, setPlanError] = React.useState("");

  // A plan belongs to the programme it was written for; keeping a stale one on
  // screen after switching offices would be worse than showing nothing.
  React.useEffect(() => { setPlan(null); setPlanError(""); }, [selected]);

  const buildPlan = async (refresh = false) => {
    setPlanning(true); setPlanError("");
    try {
      setPlan(await api.getSavingsPlan(selected, undefined, { refresh }));
    } catch (e) {
      setPlanError(e.message || String(e));
    } finally { setPlanning(false); }
  };
  const summary = useAsync(() => api.getSummary(), []);
  const calendar = useAsync(
    () => (tab === "calendar" ? api.getSavingsCalendar() : Promise.resolve(null)), [tab]);
  const usage = useAsync(
    () => (tab === "utilisation" ? api.getUtilisationMap() : Promise.resolve(null)), [tab]);
  const aispend = useAsync(
    () => (tab === "ai" ? api.getAiSpend() : Promise.resolve(null)), [tab]);
  const pipeline = useAsync(
    () => (tab === "calendar" ? api.getSavingsPipeline() : Promise.resolve(null)), [tab]);
  const funding = useAsync(() => api.getDemandFunding(), []);
  const statement = useAsync(
    () => (selected ? api.getStatement(selected) : Promise.resolve(null)), [selected]);

  if (summary.loading) return <Loading />;
  if (summary.error) return <Err>{summary.error}</Err>;
  const d = summary.data;
  const maxCost = Math.max(...d.programs.map((p) => p.cost), 1);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cares-text-1)", margin: 0,
                     display: "flex", alignItems: "center", gap: 9 }}>
          <Landmark size={20} style={{ color: "var(--cares-purple)" }} /> Chargeback
        </h1>
        <p style={{ fontSize: 13, color: "var(--cares-text-label)", margin: "6px 0 0", maxWidth: 760 }}>
          What the technology estate costs each program office, on a stated basis.
          Shared services that cannot be metered are split by staff; products with a
          single consumer are charged directly to them.
        </p>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Kpi label="Estate cost" value={money(d.estateTotal)} icon={Receipt}
             sub={`fiscal year ${d.fiscalYear}`} />
        <Kpi label="Allocated" value={money(d.allocatedTotal)} icon={PieChart}
             sub={`${d.programs.length} program offices`} />
        <Kpi label="Idle licences" value={money(d.idleTotal)} icon={AlertTriangle}
             tone="var(--cares-amber)"
             sub={`${((d.idleTotal / d.estateTotal) * 100).toFixed(0)}% of the estate`} />
        <Kpi label="Unallocated" value={d.unallocated.length} icon={FileWarning}
             tone={d.unallocated.length ? "var(--cares-orange)" : "var(--cares-green)"}
             sub={d.unallocated.length ? "products with no owner" : "every product allocated"} />
      </div>

      {/* Reconciliation is stated rather than hidden — a chargeback report that
          silently fails to tie is one nobody trusts a second time. */}
      <div style={{ ...card, background: "var(--cares-surface-board)", padding: "10px 14px", marginBottom: 14,
                    display: "flex", gap: 9, alignItems: "flex-start" }}>
        <Info size={14} style={{ color: "var(--cares-text-label)", flexShrink: 0, marginTop: 2 }} />
        <span style={{ fontSize: 11.5, color: "var(--cares-text-3)" }}>
          Allocated total differs from estate cost by{" "}
          <strong>{money(Math.abs(d.reconciliation.difference))}</strong>.{" "}
          {d.reconciliation.note}
        </span>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--cares-line-card)",
                    marginBottom: 14, flexWrap: "wrap" }}>
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  style={{ padding: "9px 14px", background: "none", border: "none",
                           cursor: "pointer", fontSize: 12.5, fontWeight: 600,
                           color: tab === t.id ? "var(--cares-purple)" : "var(--cares-text-label)",
                           borderBottom: tab === t.id ? "2px solid var(--cares-purple)"
                                                      : "2px solid transparent" }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "calendar" && pipeline.data && <SavingsPipeline d={pipeline.data} />}

      {tab === "calendar" && (
        calendar.loading ? <Loading /> : calendar.error ? <Err>{calendar.error}</Err> :
        calendar.data && (
          <div style={{ ...card, padding: 16, marginBottom: 14 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 3px" }}>
              Savings calendar
            </h3>
            <p style={{ fontSize: 11.5, color: "var(--cares-text-note)", margin: "0 0 6px" }}>
              {calendar.data.note}
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", margin: "10px 0 14px" }}>
              {calendar.data.buckets.map((b) => (
                <div key={b.window} style={{ ...card, padding: "9px 13px", minWidth: 132 }}>
                  <div style={{ fontSize: 17, fontWeight: 700,
                                color: b.window.startsWith("0-30") ? "var(--cares-orange)" : "var(--cares-text-1)" }}>
                    {money(b.idle)}
                  </div>
                  <div style={{ fontSize: 10.5, color: "var(--cares-text-label)" }}>
                    {b.window} · {b.count} renewals
                  </div>
                </div>
              ))}
            </div>
            <RenewalTimeline renewals={calendar.data.renewals} />
          </div>
        )
      )}

      {tab === "utilisation" && (
        usage.loading ? <Loading /> : usage.error ? <Err>{usage.error}</Err> :
        usage.data && (
          <div style={{ ...card, padding: 16, marginBottom: 14 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 3px" }}>
              Cost against utilisation
            </h3>
            <p style={{ fontSize: 11.5, color: "var(--cares-text-note)", margin: "0 0 10px" }}>
              Sorting by cost hides the cheap things nobody uses; sorting by
              utilisation hides the expensive ones. Both axes together is the
              only view that finds the shortlist.
            </p>
            <UtilisationQuadrant products={usage.data.products} />
            {usage.data.actNow.length > 0 && (
              <div style={{ marginTop: 12, padding: 11, borderRadius: 8,
                            background: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", border: "1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))",
                            fontSize: 12, color: "var(--cares-orange)" }}>
                <strong>{usage.data.actNow.length} products are costly, less than half
                used, and renew within 120 days:</strong>{" "}
                {usage.data.actNow.map((p) => p.product).join(", ")}.
              </div>
            )}
          </div>
        )
      )}

      {tab === "ai" && (
        aispend.loading ? <Loading /> : aispend.error ? <Err>{aispend.error}</Err> :
        aispend.data && <AiExposure d={aispend.data} />
      )}

      {tab === "allocation" && (
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "flex-start" }}>
        {/* ---------------------------------------------------- programs */}
        <div style={{ ...card, flex: "1 1 520px", minWidth: 0, overflow: "hidden" }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0, padding: "13px 14px 9px" }}>
            Cost by program office
          </h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Program</th><th style={th}>Annual charge</th>
                <th style={th}>Share</th><th style={th}>Idle</th>
                <th style={th}>Per staff</th>
              </tr></thead>
              <tbody>
                {d.programs.map((p) => {
                  const idlePct = p.cost ? (p.idleCost / p.cost) * 100 : 0;
                  const on = selected === p.program_code;
                  return (
                    <tr key={p.program_code}
                        onClick={() => setSelected(on ? null : p.program_code)}
                        style={{ cursor: "pointer",
                                 background: on ? "color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))" : undefined }}>
                      <td style={td}>
                        <div style={{ fontWeight: 700, color: "var(--cares-text-1)" }}>{p.program_code}</div>
                        <div style={{ fontSize: 10.5, color: "var(--cares-text-note)" }}>
                          {p.program_name}
                        </div>
                        <div style={{ fontSize: 9.5, color: "var(--cares-text-dim)" }}>
                          {p.appropriation}
                        </div>
                      </td>
                      <td style={{ ...td, fontWeight: 600, whiteSpace: "nowrap" }}>
                        {money(p.cost)}
                      </td>
                      <td style={{ ...td, minWidth: 90 }}>
                        <div style={{ fontSize: 11, color: "var(--cares-text-3)", marginBottom: 3 }}>
                          {p.sharePct}%
                        </div>
                        <ShareBar pct={(p.cost / maxCost) * 100} />
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        <div style={{ color: idlePct > 30 ? "var(--cares-orange)" : "var(--cares-amber)",
                                      fontWeight: idlePct > 30 ? 700 : 500 }}>
                          {money(p.idleCost)}
                        </div>
                        <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>
                          {idlePct.toFixed(0)}% of bill
                        </div>
                      </td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>
                        {p.costPerStaff ? money(p.costPerStaff) : "—"}
                        <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>
                          {n(p.staff_fte)} staff
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 11, color: "var(--cares-text-note)", padding: "9px 14px 12px", margin: 0 }}>
            Select a program to see its itemised statement.
          </p>
        </div>

        {/* --------------------------------------------------- statement */}
        <div style={{ ...card, flex: "1 1 380px", minWidth: 0, overflow: "hidden" }}>
          {!selected ? (
            <div style={{ padding: 22, color: "var(--cares-text-note)", fontSize: 12.5, lineHeight: 1.6 }}>
              <Receipt size={16} style={{ marginBottom: 6 }} />
              <p style={{ margin: 0 }}>
                Pick a program office to see every product it is charged for, the
                basis for each charge, and how much of it is buying licences
                nobody uses.
              </p>
            </div>
          ) : statement.loading ? <Loading />
            : statement.error ? <Err>{statement.error}</Err>
            : statement.data && (
            <>
              <div style={{ padding: "13px 14px 10px", borderBottom: "1px solid var(--cares-line)" }}>
                <h3 style={{ fontSize: 13.5, fontWeight: 700, margin: 0 }}>
                  {statement.data.program.program_name}
                </h3>
                <div style={{ fontSize: 11, color: "var(--cares-text-note)", marginTop: 2 }}>
                  {statement.data.program.appropriation}
                </div>
                <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--cares-text-1)" }}>
                      {money(statement.data.total)}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>annual charge</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--cares-amber)" }}>
                      {statement.data.idleSharePct}%
                    </div>
                    <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>on idle licences</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--cares-text-3)" }}>
                      {money(statement.data.directSpend)}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>directly theirs</div>
                  </div>
                </div>
                <button onClick={buildPlan} disabled={planning}
                        style={{ ...btn, marginTop: 12, background: "var(--cares-purple)",
                                 borderColor: "var(--cares-purple)", color: "var(--cares-text-max)" }}>
                  {planning ? <Loader2 size={13} /> : <Sparkles size={13} />}
                  {planning ? "Writing the plan…" : "Build a savings plan"}
                </button>
                {planError && (
                  <div style={{ marginTop: 9, padding: 9, borderRadius: 7,
                                background: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", border: "1px solid color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))",
                                fontSize: 11.5, color: "var(--cares-orange)" }}>
                    {planError}
                  </div>
                )}
              </div>
              {statement.data?.fundsControl && (
                <FundsControl fc={statement.data.fundsControl} />
              )}
              {plan && (
                <div style={{ padding: "12px 14px", background: "var(--cares-surface-board)",
                              borderBottom: "1px solid var(--cares-line)" }}>
                  <Plan text={plan.plan} />
                  <div style={{ fontSize: 10.5, color: "var(--cares-text-note)", marginTop: 10 }}>
                    Written from {money(plan.basedOn.annualCharge)} of charges and{" "}
                    {plan.basedOn.renewalsConsidered} renewals inside twelve months.
                    {/* A replayed plan is the real generated text, not a substitute -
                        but saying when it was written keeps that honest, and lets
                        anyone force a fresh one. */}
                    {plan.cached && (
                      <>
                        {" · Written "}
                        {plan.generatedAt
                          ? new Date(plan.generatedAt).toLocaleDateString(undefined,
                              { day: "numeric", month: "short", year: "numeric" })
                          : "earlier"}
                        {", replayed. "}
                        <button onClick={() => buildPlan(true)} disabled={planning}
                                style={{ background: "none", border: "none", padding: 0,
                                         font: "inherit", color: "var(--cares-purple)",
                                         textDecoration: "underline",
                                         cursor: planning ? "default" : "pointer" }}>
                          Write a new one
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
              <div style={{ overflowX: "auto", maxHeight: 420 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={th}>Product</th><th style={th}>Basis</th>
                    <th style={th}>Share</th><th style={th}>Charge</th>
                  </tr></thead>
                  <tbody>
                    {statement.data.lines.map((l, i) => (
                      <tr key={i}>
                        <td style={td}>
                          <div style={{ fontWeight: 600, color: "var(--cares-text-1)", fontSize: 11.5 }}>
                            {l.product}
                          </div>
                          <div style={{ fontSize: 10, color: "var(--cares-text-note)" }}>
                            {l.manufacturer}
                          </div>
                        </td>
                        <td style={td}>
                          <Pill tone={BASIS_TONE[l.basis] || BASIS_TONE.headcount}>
                            {l.basis}
                          </Pill>
                          {l.units ? (
                            <div style={{ fontSize: 9.5, color: "var(--cares-text-note)", marginTop: 3 }}>
                              {n(l.units)} units
                            </div>
                          ) : null}
                        </td>
                        <td style={{ ...td, fontSize: 11 }}>{l.allocationPct}%</td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          <div style={{ fontWeight: 600 }}>{money(l.charge)}</div>
                          {l.idleCharge > 0 && (
                            <div style={{ fontSize: 10, color: "var(--cares-amber)" }}>
                              {money(l.idleCharge)} idle
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      )}

      {/* ----------------------------------------------- demand funding */}
      {!funding.loading && !funding.error && funding.data && (
        <div style={{ ...card, padding: 16, marginTop: 14 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: "0 0 4px",
                       display: "flex", alignItems: "center", gap: 7 }}>
            <Users size={14} style={{ color: "var(--cares-purple)" }} /> Funding for work in flight
          </h3>
          <p style={{ fontSize: 11.5, color: "var(--cares-text-label)", margin: "0 0 10px" }}>
            {funding.data.note}
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Demand</th><th style={th}>Stage</th>
                <th style={th}>Funding program</th>
              </tr></thead>
              <tbody>
                {funding.data.demands.map((r) => (
                  <tr key={r.demand_id}>
                    <td style={td}>
                      <span style={{ fontFamily: "ui-monospace, Menlo, monospace",
                                     fontSize: 11 }}>{r.demand_id}</span>
                      <div style={{ fontSize: 10.5, color: "var(--cares-text-note)" }}>
                        {String(r.title).slice(0, 46)}
                      </div>
                    </td>
                    <td style={{ ...td, fontSize: 11 }}>{r.current_stage}</td>
                    <td style={td}>
                      {r.fundingIdentified
                        ? <Pill tone={BASIS_TONE.direct}>{r.fundingProgram}</Pill>
                        : <span style={{ color: "var(--cares-orange)", fontSize: 11, fontWeight: 600 }}>
                            not identified
                          </span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
